import {
    Journal,
    JournalLinesFields,
    JournalStatusEnum,
    JournalTaxTypeEnum,
    JournalTotals,
    NewJournalReq,
    NewOrEditJournalFields,
    UpdateJournalLineReq,
    UpdateJournalReq,
} from '../../types/journal';
import { NatureEnum } from '../../types/nature';
import { ReconciliationWorksheet } from '../../types/worksheet';
import { formatDateNumeric } from '../../utilities/formatting';
import { isAllNullOrUndefined, isNullOrUndefined } from '../new-workpaper/utils';

/**
 * Format journal form data for validation
 *
 * @param values New journal data fields
 * @param status New journal status
 * @returns NewJournalReq
 */
export function formatNewJournalFormData(values: NewOrEditJournalFields, status: JournalStatusEnum): NewJournalReq {
    const journal = {
        status,
        date: values.date || undefined,
        narration: values.narration || undefined,
        taxType: values.taxType || JournalTaxTypeEnum.NO_TAX,
        debitTotal: values.debitTotal === undefined ? undefined : roundToTwoDecimal({ amount: values.debitTotal }),
        creditTotal: values.creditTotal === undefined ? undefined : roundToTwoDecimal({ amount: values.creditTotal }),
        lines: values.lines
            .filter((jl) => !isAllNullOrUndefined([jl.description, jl.debit, jl.credit, jl.account]))
            .map((jl, idx) => {
                return {
                    sequence: idx + 1,
                    description: jl.description || undefined,
                    account: jl.account || undefined,
                    taxRate: jl.taxRate?.id || undefined,
                    lineType: jl.debit === null ? NatureEnum.CREDIT : NatureEnum.DEBIT,
                    lineAmount: jl.debit === null ? (jl.credit === null ? null : +jl.credit) : +jl.debit,
                };
            }),
    };

    return journal;
}

/**
 * Format journal form data for validation
 *
 * @param values Existing and journal data fields
 * @param status Updated journal status
 * @param journalId Journal id
 * @returns UpdateJournalReq
 */
export function formatExistingJournalFormData(
    values: NewOrEditJournalFields,
    status: JournalStatusEnum,
    journalId: string
): UpdateJournalReq {
    const journal = {
        id: journalId,
        status,
        date: values.date,
        narration: values.narration,
        taxType: values.taxType || JournalTaxTypeEnum.NO_TAX,
        debitTotal: values.debitTotal === undefined ? undefined : roundToTwoDecimal({ amount: values.debitTotal }),
        creditTotal: values.creditTotal === undefined ? undefined : roundToTwoDecimal({ amount: values.creditTotal }),
        lines: values.lines
            .filter((jl) => !isAllNullOrUndefined([jl.description, jl.debit, jl.credit, jl.account]))
            .map((jl, idx) => {
                return {
                    id: jl.id.includes('row-') ? undefined : jl.id,
                    fields: {
                        sequence: idx + 1,
                        description: jl.description,
                        account: jl.account,
                        taxRate: jl.taxRate?.id ? jl.taxRate.id : null,
                        lineType: jl.debit === null ? NatureEnum.CREDIT : NatureEnum.DEBIT,
                        lineAmount: jl.debit === null ? (jl.credit === null ? null : +jl.credit) : +jl.debit,
                    },
                };
            }),
    };

    return journal;
}

/**
 * Validates a journal has all required fields.
 *
 * @param journal
 * @returns validation error message
 */
export function validateNewJournal(journal: NewJournalReq): string | undefined {
    if (!journal.date && !journal.narration && !journal.taxType && journal.lines.length === 0) {
        return 'A journal cannot be empty. Please enter some values.';
    }

    if (!journal.date) {
        return 'A journal must have a date.';
    }

    if (!journal.narration) {
        return 'A journal must have a narration.';
    }

    if (journal.lines.some((line) => !line.lineAmount || !line.account || !line.description)) {
        return 'Journal lines must have an account, description and debit/credit value must not be zero or empty.';
    }

    if (journal.lines.some((line) => journal.taxType !== JournalTaxTypeEnum.NO_TAX && !line.taxRate)) {
        return 'Journal lines must have a tax rate.';
    }

    if (!journal.creditTotal || !journal.debitTotal) {
        return 'Debits total and credits total must not be zero or empty.';
    }

    if (journal.creditTotal !== journal.debitTotal) {
        return 'Debits total must equal to the credits total.';
    }
}

/**
 * Validate a journal has all required fields.
 * Combine value from existing journal and new value input
 *
 * @param journal
 * @returns Validation error message
 */
export function validateExistingJournal(journal: UpdateJournalReq): string | undefined {
    if (!journal.date && !journal.narration && !journal.taxType && (!journal.lines || journal.lines.length === 0)) {
        return 'A journal cannot be empty. Please enter some values.';
    }

    if (!journal.date) {
        return 'A journal must have a date.';
    }

    if (!journal.narration) {
        return 'A journal must have a narration.';
    }

    if (journal.lines) {
        if (journal.lines.some((line) => !line.fields?.lineAmount || !line.fields?.account || !line.fields?.description)) {
            return 'Journal lines must have an account, description and debit/credit value must not be zero or empty.';
        }

        if (journal.lines.some((line) => journal.taxType !== JournalTaxTypeEnum.NO_TAX && !line.fields?.taxRate)) {
            return 'Journal lines must have a tax rate.';
        }
    }

    if (!journal.creditTotal || !journal.debitTotal) {
        return 'Debits total and credits total must not be zero or empty.';
    }

    if (journal.creditTotal !== journal.debitTotal) {
        return 'Debits total must equal to the credits total.';
    }
}

/**
 * Add zero to single digit number to ensure day and month is in correct format
 */
function addLeadingZero(value: number): string {
    return value > 9 ? `${value}` : `0${value}`;
}

/**
 * Calculates the year of previous period end date.
 *
 * @param periodEnd Period end Date object
 * @param financialYearEndMonth Financial year end month of an entity
 * @param financialYearEndDay Financial year end day of an entity
 * @returns prevYear
 */
export function determinePreviousTrialBalanceYear(periodEnd: Date, financialYearEndMonth: number, financialYearEndDay: number): number {
    const workpaperPeriodEndYear = periodEnd.getFullYear();
    const workpaperPeriodEndMonth = periodEnd.getMonth() + 1; // Javascript months are 0 - 11
    const workpaperPeriodEndDay = periodEnd.getDate();

    // If the period end month of the new workpaper is greater than the saved financial year end month, set the calendar year to minus 1.
    // e.g. if the period end of the workpaper is 2021-06-30 and the entity financial year end month is 6 (AUS), the previous date will be 2020-06-30.
    // e.g. if the period end of the workpaper is 2021-06-30 and the entity financial year end month is 3 (NZ), the previous date will be 2021-03-31.
    // e.g. if the period end of the workpaper is 2021-06-30 and the entity financial year end month is 4 (UK), the previous

    // By default, set the previous year to be 1 less than the workpaper period end month.
    let prevYear = workpaperPeriodEndYear - 1;

    // If the workpaper period end month is greater than the entity financial year end month, reset the year the workpaperPeriodEndMonth.
    if (workpaperPeriodEndMonth > financialYearEndMonth) {
        prevYear = workpaperPeriodEndYear;
    } else if (workpaperPeriodEndMonth === financialYearEndMonth) {
        // Reset the prev year back to the workpaperPeriodEndYear if the workpaperPeriodEndDay is greater than the entity's financial year end day.
        if (workpaperPeriodEndDay > financialYearEndDay) {
            prevYear = workpaperPeriodEndYear;
        }
    }

    return prevYear;
}

/**
 * Formats the period end date into the YYYY-MM-DD format.
 *
 * @param periodEnd Period end Date object
 * @returns YYYY-MM-DD string
 */
export function formatCurrentPeriodEndDate(periodEnd: Date): string {
    const workpaperPeriodEndYear = periodEnd.getFullYear();
    const workpaperPeriodEndMonth = periodEnd.getMonth() + 1; // Javascript months are 0 - 11
    const workpaperPeriodEndDay = periodEnd.getDate();

    const periodEndDate = `${workpaperPeriodEndYear}-${addLeadingZero(workpaperPeriodEndMonth)}-${addLeadingZero(workpaperPeriodEndDay)}`;
    return periodEndDate;
}

/**
 * Formats the period start date for a current year into the YYYY-MM-DD format.
 *
 * @param periodEnd Previous period end date object
 * @returns YYYY-MM-DD string
 */
export function formatCurrentPeriodStartDate(previousEndDate: Date): string {
    const startLY = new Date(previousEndDate);
    startLY.setDate(startLY.getDate() + 1);

    const workpaperCurrentStartYear = startLY.getFullYear();
    const workpaperCurrentStartMonth = startLY.getMonth() + 1; // Javascript months are 0 - 11
    const workpaperCurrentStartDay = startLY.getDate();

    const currentPeriodStartDate = `${workpaperCurrentStartYear}-${addLeadingZero(workpaperCurrentStartMonth)}-${addLeadingZero(
        workpaperCurrentStartDay
    )}`;

    return currentPeriodStartDate;
}

/**
 * Formats the period end date for a previous year into the YYYY-MM-DD format.
 *
 * @param periodEnd Period end Date object
 * @param financialYearEndMonth Financial year end month of an entity
 * @param financialYearEndDay Financial year end day of an entity
 * @returns YYYY-MM-DD string
 */
export function formatPreviousPeriodEndDate(periodEnd: Date, financialYearEndMonth: number, financialYearEndDay: number): string {
    const prevYear = determinePreviousTrialBalanceYear(periodEnd, financialYearEndMonth, financialYearEndDay);
    const prevPeriodEndDate = `${prevYear}-${financialYearEndMonth}-${financialYearEndDay}`;
    return prevPeriodEndDate;
}

/**
 * Calculate the start and end date for each period
 *
 * @param periodEnd Workpaper period end
 * @returns startLY, endLY, endTY
 */
export function calcWorkpaperPeriod(
    periodEnd: Date,
    financialYearEndMonth: number,
    financialYearEndDay: number
): { startDate: string; endDate: string } {
    const endLY = formatPreviousPeriodEndDate(periodEnd, financialYearEndMonth, financialYearEndDay);
    const endTY = formatCurrentPeriodEndDate(periodEnd);
    const startTY = formatCurrentPeriodStartDate(new Date(endLY));

    return { startDate: startTY, endDate: endTY };
}

/**
 * Calculate the remaining amount including tax to balance the journal
 *
 * @param debitTotal Journal debit total
 * @param creditTotal Journal credit total
 * @returns Debit remaining, credit remaining
 */
function calcRemainingAmount(debitTotal: number, creditTotal: number): { debitRemaining: number | null; creditRemaining: number | null } {
    const total = roundToTwoDecimal({ amount: debitTotal, inCents: true }) + roundToTwoDecimal({ amount: creditTotal, inCents: true }) * -1;
    const remaining = 0 - total;
    let debitRemaining: number | null = null;
    let creditRemaining: number | null = null;

    if (remaining < 0) {
        creditRemaining = roundToTwoDecimal({ amount: Math.abs(remaining) / 100 });
    } else if (remaining > 0) {
        debitRemaining = roundToTwoDecimal({ amount: Math.abs(remaining) / 100 });
    }

    return { creditRemaining, debitRemaining };
}

/**
 * Calculate the remaining amount excluding tax to display on journal line
 *
 * @param taxType Journal tax type
 * @param journalLines Journal lines
 * @param lineToPopulate Journal line that needs to be populated
 * @returns Debit remaining, credit remaining
 */
export function calcRemainingAmountToDisplay(
    taxType: JournalTaxTypeEnum,
    journalLines: JournalLinesFields[],
    lineToPopulate: JournalLinesFields
): { debitRemaining: number | null; creditRemaining: number | null } {
    const { debitTotal, creditTotal } = calcJournalTotal(
        taxType,
        lineToPopulate?.amountEditable === false ? journalLines.filter((l) => l.amountEditable) : journalLines
    );

    if (taxType === JournalTaxTypeEnum.EXCLUSIVE) {
        const { debitRemaining, creditRemaining } = calcRemainingAmount(debitTotal * 100, creditTotal * 100);

        let newDebitRemaining = debitRemaining;
        let newCreditRemaining = creditRemaining;

        const taxRate = lineToPopulate?.taxRate?.rate;
        if (taxRate && taxType === JournalTaxTypeEnum.EXCLUSIVE) {
            if (newDebitRemaining !== null) {
                newDebitRemaining =
                    roundToTwoDecimal({ amount: newDebitRemaining - newDebitRemaining * (taxRate / (taxRate + 100)) }) || null;
            } else if (newCreditRemaining !== null) {
                newCreditRemaining =
                    roundToTwoDecimal({ amount: newCreditRemaining - newCreditRemaining * (taxRate / (taxRate + 100)) }) || null;
            }
        }

        return { debitRemaining: newDebitRemaining, creditRemaining: newCreditRemaining };
    } else {
        return calcRemainingAmount(debitTotal * 100, creditTotal * 100);
    }
}

/**
 * Calculate the sum of debit and credit amount from every journal line
 *
 * @param journalLines JournalLinesFields[]
 * @returns Debit amount, credit amount
 */
function calcLinesTotal(journalLines: JournalLinesFields[]): { debitAmount: number; creditAmount: number } {
    const debitAmount = journalLines.reduce((acc, curr) => acc + (curr.debit ? curr.debit * 100 : 0), 0);
    const creditAmount = journalLines.reduce((acc, curr) => acc + (curr.credit ? curr.credit * 100 : 0), 0);

    return { debitAmount, creditAmount };
}

/**
 * Calculate journal total values (tax, subtotal, total)
 *
 * @param taxType Journal tax type
 * @param journalLines Journal lines
 * @returns JournalTotals
 */
export function calcJournalTotal(taxType: JournalTaxTypeEnum, journalLines: JournalLinesFields[]): JournalTotals {
    if (taxType === JournalTaxTypeEnum.EXCLUSIVE) {
        const { debitAmount: debitSubtotal, creditAmount: creditSubtotal } = calcLinesTotal(journalLines);

        let debitTaxTotal = 0;
        let creditTaxTotal = 0;
        for (const line of journalLines) {
            if (line.debit && line.taxRate?.rate && line.taxRate.rate > 0) {
                debitTaxTotal += roundToTwoDecimal({ amount: line.debit * 100 * (line.taxRate.rate / 100), inCents: true });
            } else if (line.credit && line.taxRate?.rate && line.taxRate.rate > 0) {
                creditTaxTotal += roundToTwoDecimal({ amount: line.credit * 100 * (line.taxRate.rate / 100), inCents: true });
            }
        }

        const debitTotal = debitSubtotal + debitTaxTotal;
        const creditTotal = creditSubtotal + creditTaxTotal;

        return parseAllCentsToDollar({ debitTotal, creditTotal, debitSubtotal, creditSubtotal, debitTaxTotal, creditTaxTotal });
    } else if (taxType === JournalTaxTypeEnum.INCLUSIVE) {
        const { debitAmount: debitTotal, creditAmount: creditTotal } = calcLinesTotal(journalLines);

        let debitTaxTotal = 0;
        let creditTaxTotal = 0;
        for (const line of journalLines) {
            if (line.debit && line.taxRate?.rate && line.taxRate.rate > 0) {
                debitTaxTotal += roundToTwoDecimal({
                    amount: line.debit * 100 * (line.taxRate.rate / (100 + line.taxRate.rate)),
                    inCents: true,
                });
            } else if (line.credit && line.taxRate?.rate && line.taxRate.rate > 0) {
                creditTaxTotal += roundToTwoDecimal({
                    amount: line.credit * 100 * (line.taxRate.rate / (100 + line.taxRate.rate)),
                    inCents: true,
                });
            }
        }

        const debitSubtotal = debitTotal - debitTaxTotal;
        const creditSubtotal = creditTotal - creditTaxTotal;

        return parseAllCentsToDollar({ debitTotal, creditTotal, debitSubtotal, creditSubtotal, debitTaxTotal, creditTaxTotal });
    } else {
        const { debitAmount: debitTotal, creditAmount: creditTotal } = calcLinesTotal(journalLines);

        return parseAllCentsToDollar({ debitTotal, creditTotal });
    }
}

/**
 * Pass all cent values for journal totals into dollar
 *
 * @param amount JournalTotals in cents
 * @returns JournalTotals in dollar
 */
function parseAllCentsToDollar(amount: JournalTotals): JournalTotals {
    const newTotal: JournalTotals = { debitTotal: 0, creditTotal: 0 };

    for (const field of Object.keys(amount)) {
        newTotal[field as keyof JournalTotals] = (amount[field as keyof JournalTotals] as number) / 100;
    }

    return newTotal;
}

/**
 * Pass a single amount into dollar
 *
 * @param amount Cents amount
 * @returns Dollar amount
 */
export function parseToDollar(amount: number): number {
    return amount / 100;
}

/**
 * Round an amount to 2 decimal place
 *
 * @param amount Amount to be rounded
 * @param inCents If amount is not in cents, multiply by hundred before roudning
 * @returns Rounded amount
 */
export function roundToTwoDecimal({ amount, inCents = false }: { amount: number; inCents?: boolean }): number {
    if (inCents) {
        return Math.round(amount);
    }
    return Math.round(amount * 100) / 100;
}

/**
 * Format default journal to be provided for journal form
 *
 * @param emptyLines Empty journal lines
 * @param worksheet Worksheet
 * @param defaultJournal Default journal data
 * @returns NewOrEditJournalFields
 */
export function formatDefaultJournal(emptyLines: JournalLinesFields[], defaultJournal?: Journal): NewOrEditJournalFields {
    let defaultLines: JournalLinesFields[];

    defaultLines =
        defaultJournal?.lines.map((l) => {
            const lineAmount = defaultJournal.taxType === JournalTaxTypeEnum.INCLUSIVE ? l.lineTotal : l.lineAmount;
            const line: JournalLinesFields = {
                id: l.id,
                sequence: l.sequence,
                description: l.description || null,
                account: l.account?.entityAccountId || null,
                debit: l.lineType === NatureEnum.DEBIT && !isNullOrUndefined(lineAmount) ? parseToDollar(lineAmount) : null,
                credit: l.lineType === NatureEnum.CREDIT && !isNullOrUndefined(lineAmount) ? parseToDollar(lineAmount) : null,
                amountEditable: true,
                taxRate: { id: l.taxRate?.id, name: l.taxRate?.name, rate: l.taxRate?.rate },
            };

            return line;
        }) ?? [];

    // Add empty lines to fill up to 2 lines minimum if default journal has less than 2 lines
    if (defaultJournal) {
        if (defaultLines.length === 0) {
            defaultLines = emptyLines;
        } else if (defaultLines.length === 1) {
            defaultLines = defaultLines.concat([emptyLines[1]]);
        }
    }

    const defaultValues: NewOrEditJournalFields = {
        narration: defaultJournal?.narration || null,
        date: defaultJournal?.date ? formatDateNumeric(defaultJournal?.date, true) : null,
        taxType: defaultJournal?.taxType,
        debitTotal: defaultJournal?.total?.debit !== undefined ? parseToDollar(defaultJournal.total.debit) : undefined,
        creditTotal: defaultJournal?.total?.credit !== undefined ? parseToDollar(defaultJournal.total.credit) : undefined,
        debitSubtotal: defaultJournal?.subtotal?.debit !== undefined ? parseToDollar(defaultJournal.subtotal.debit) : undefined,
        creditSubtotal: defaultJournal?.subtotal?.credit !== undefined ? parseToDollar(defaultJournal.subtotal.credit) : undefined,
        debitTaxTotal: defaultJournal?.taxTotal?.debit !== undefined ? parseToDollar(defaultJournal.taxTotal.debit) : undefined,
        creditTaxTotal: defaultJournal?.taxTotal?.credit !== undefined ? parseToDollar(defaultJournal.taxTotal.credit) : undefined,
        valueOutBy: parseToDollar(
            roundToTwoDecimal({ amount: (defaultJournal?.total?.debit || 0) - (defaultJournal?.total?.credit || 0), inCents: true })
        ),
        lines: defaultLines,
    };

    return defaultValues;
}

/**
 * Format journal lines to be updated with only fields that have changed
 *
 * @param defaultValues NewOrEditJournalFields
 * @param journalToUpdate UpdateJournalReq
 * @returns UpdateJournalLineReq[] if lines details changed, else undefined
 */
export function formatUpdatedJournalLines(
    defaultValues: NewOrEditJournalFields,
    journalToUpdate: UpdateJournalReq
): UpdateJournalLineReq[] | undefined {
    const updatedJournalLines: UpdateJournalLineReq[] | undefined = journalToUpdate.lines
        ?.filter(
            (jl) =>
                !isAllNullOrUndefined([
                    jl.fields?.description,
                    jl.fields?.lineAmount,
                    jl.fields?.taxRate,
                    jl.fields?.account,
                    jl.fields?.lineType,
                    jl.fields?.sequence,
                ])
        )
        .map((jl) => {
            const matchedLine = defaultValues.lines.find((l) => l.id === jl.id);

            let currentLineType: NatureEnum | undefined;

            if (!isNullOrUndefined(matchedLine?.debit)) {
                currentLineType = NatureEnum.DEBIT;
            } else if (!isNullOrUndefined(matchedLine?.credit)) {
                currentLineType = NatureEnum.CREDIT;
            }

            let currentLineAmount: number | null | undefined;

            if (matchedLine && !isNullOrUndefined(matchedLine.credit)) {
                currentLineAmount = matchedLine.credit;
            } else if (matchedLine && !isNullOrUndefined(matchedLine.debit)) {
                currentLineAmount = matchedLine.debit;
            }

            return {
                id: jl.id,
                fields: {
                    sequence: matchedLine?.sequence === jl.fields?.sequence ? undefined : jl.fields?.sequence,
                    description: matchedLine?.description === jl.fields?.description ? undefined : jl.fields?.description,
                    account: matchedLine?.account === jl.fields?.account ? undefined : jl.fields?.account,
                    taxRate: matchedLine?.taxRate?.id === jl.fields?.taxRate ? undefined : jl.fields?.taxRate,
                    lineType: currentLineType === jl.fields?.lineType ? undefined : jl.fields?.lineType,
                    lineAmount: currentLineAmount === jl.fields?.lineAmount ? undefined : jl.fields?.lineAmount,
                },
            };
        });

    return updatedJournalLines;
}
